import { styled } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ListBoundary } from "src/components/ListBoundary/ListBoundary";
import StyledTable from "src/components/Table";
import { AppDispatch, RootState } from "src/store";
import colors from "src/theme/colors";
import { GridColDef } from "@mui/x-data-grid";
import { Eye } from "iconsax-react";

import {
  deleteSaleOrderShipmentCarrier,
  getSaleOrderShipmentCarriers,
  addSaleOrderShipmentCarrier,
  reset as resetSaleOrderCarriers,
} from "src/features/saleOrders/redux/CarriersSlice";
import { StyledLoadingButton } from "src/components/LoadingButton";
import ConfirmModal from "src/components/Modal";
import { AddNewTruckModal } from "../modals/AddNewTruckModal";

const filtersList = [
  {
    id: 1,
    label: "loading",
    title: "In Progress",
  },
  {
    id: 2,
    label: "dispatched,reached",
    title: "Completed",
  },
];

interface ShippingOriginWarehouseTrucksListProps {
  saleOrderId: number;
  shipmentId: number;
  warehouseId: number;
  saleOrderName: string;
}

export const ShippingOriginWarehouseTrucksList: FC<
  ShippingOriginWarehouseTrucksListProps
> = (props) => {
  const { saleOrderId, saleOrderName, warehouseId, shipmentId } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("loading");
  const [trucksData, setTrucksData] = useState([] as any);
  const [trucksMeta, setTrucksMeta] = useState([] as any);
  const [trucksLoadind, setTrucksLoadind] = useState(true);

  const [isDeletetModalOpen, setIsDeletetModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [selectedName, setSelectedName] = useState("");

  const [isAddNewTruckModalOpen, setIsAddNewTruckModalOpen] = useState(false);

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Truck Reg Number",
      flex: 1,
      minWidth: 140,
      filterable: false,
    },
    {
      field: "total_products",
      headerName: "Total Products",
      flex: 1,
      minWidth: 100,
      filterable: false,
    },
    {
      field: "total_weight",
      headerName: "Total Products Weight",
      flex: 1,
      minWidth: 100,
      filterable: false,
    },
    {
      field: "documents",
      headerName: "Documents",
      flex: 1,
      minWidth: 120,
      filterable: false,
      renderCell: (params) => (
        <Box padding={2} color={"#1E2329"}>
          {params.row?.full_weight ? params.row?.full_weight * 1000 : 0}
        </Box>
      ),
    },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 0.2,
      minWidth: 120,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="space-around"
          alignItems="center"
          flexDirection="row"
        >
          <IconButton
            onClick={() => {
              navigate(
                "/saleOrders/saleOrders-information/origin-carrier-information",
                {
                  state: {
                    data: params?.row,
                    warehouseId: warehouseId,
                    saleOrderName: saleOrderName,
                  },
                }
              );
            }}
          >
            <Eye color={colors.gray[600]} size="24" />
          </IconButton>
          {/* <IconButton>
            <img
              src={process.env.PUBLIC_URL + "/assets/icons/Remove.svg"}
              alt="delete"
              onClick={() => {
                setSelectedId(Number(params?.id));
                setSelectedName(params?.row?.name);
                setIsDeletetModalOpen(true);
              }}
            />
          </IconButton> */}
        </Box>
      ),
    },
  ];

  const {
    shipmentCarriers,
    shipmentCarriersMeta,
    isGetShipmentCarriersError,
    isDeleteShipmentCarriersError,
    isAddShipmentCarriersError,
    isGetShipmentCarriersSuccess,
    isDeleteShipmentCarriersSuccess,
    isAddShipmentCarriersSuccess,
    isGetShipmentCarriersLoading,
    isDeleteShipmentCarriersLoading,
    isAddShipmentCarriersLoading,
    getShipmentCarriersMessage,
    deleteShipmentCarriersMessage,
    addShipmentCarriersMessage,
  } = useSelector((state: RootState) => state.carriers);

  useEffect(() => {
    if (isGetShipmentCarriersError) {
      enqueueSnackbar(getShipmentCarriersMessage, { variant: "error" });
    }
    if (isGetShipmentCarriersSuccess) {
      setTrucksData(shipmentCarriers);
      setTrucksMeta(shipmentCarriersMeta);
      setTrucksLoadind(false);
    }
    return () => {
      dispatch(resetSaleOrderCarriers());
    };
  }, [
    isGetShipmentCarriersError,
    isGetShipmentCarriersSuccess,
    getShipmentCarriersMessage,
    dispatch,
  ]);

  useEffect(() => {
    if (isAddShipmentCarriersError) {
      enqueueSnackbar(addShipmentCarriersMessage, { variant: "error" });
    }
    if (isAddShipmentCarriersSuccess) {
      enqueueSnackbar(addShipmentCarriersMessage, { variant: "success" });
      dispatch(
        getSaleOrderShipmentCarriers({
          saleOrderId: Number(saleOrderId),
          shipmentId: Number(shipmentId),
          filter: filter,
          page: page,
        })
      ).then(() => setIsAddNewTruckModalOpen(false));
    }
    return () => {
      dispatch(resetSaleOrderCarriers());
    };
  }, [
    isAddShipmentCarriersError,
    isAddShipmentCarriersSuccess,
    addShipmentCarriersMessage,
  ]);

  useEffect(() => {
    if (isDeleteShipmentCarriersError) {
      enqueueSnackbar(deleteShipmentCarriersMessage, { variant: "error" });
    }
    if (isDeleteShipmentCarriersSuccess) {
      enqueueSnackbar(deleteShipmentCarriersMessage, { variant: "success" });
      dispatch(
        getSaleOrderShipmentCarriers({
          saleOrderId: Number(saleOrderId),
          shipmentId: Number(shipmentId),
          filter: filter,
          page: trucksData.length <= 1 ? (page > 1 ? page - 1 : 1) : page,
        })
      ).then(() => setIsDeletetModalOpen(false));

      if (trucksData.length <= 1) {
        setPage(page > 1 ? page - 1 : 1);
      }
    }
    return () => {
      dispatch(resetSaleOrderCarriers());
    };
  }, [
    isDeleteShipmentCarriersError,
    isDeleteShipmentCarriersSuccess,
    deleteShipmentCarriersMessage,
  ]);

  useEffect(() => {
    const infoValue = {
      saleOrderId: Number(saleOrderId),
      shipmentId: Number(shipmentId),
      page: page,
      filter: filter,
    };
    dispatch(getSaleOrderShipmentCarriers(infoValue));
  }, [page, filter]);

  const onSendTruckToPort = () => {
    //
  };

  const onDelete = (selected: any) => {
    const deleteValues = {
      saleOrderId: Number(saleOrderId),
      shipmentId: Number(shipmentId),
      carrierId: Number(selected),
    };
    dispatch(deleteSaleOrderShipmentCarrier(deleteValues));
  };

  const onAddNewCarrier = (regNumber: string) => {
    const AddValues = {
      saleOrderId: Number(saleOrderId),
      shipmentId: Number(shipmentId),
      truckRegNumber: regNumber,
    };
    dispatch(addSaleOrderShipmentCarrier(AddValues));
  };

  return (
    <>
      <StyledHeader>
        <Filters>
          <FiltersLabel>Trucks List: </FiltersLabel>
          {filtersList.map((filterItem) =>
            filter === filterItem.label ? (
              <StyledAcitve key={filterItem.id}>
                <Typography
                  color={colors.secondary[700]}
                  fontSize={15}
                  fontWeight="700"
                >
                  {filterItem.title}
                </Typography>
                <FilterBadge>{trucksData.length ?? 0}</FilterBadge>
              </StyledAcitve>
            ) : (
              <Typography
                key={filterItem.id}
                color={colors.gray[850]}
                fontSize={15}
                fontWeight="400"
                onClick={() => {
                  setTrucksLoadind(true);
                  setPage(1);
                  setFilter(filterItem.label);
                }}
                sx={{
                  cursor: "pointer",
                }}
              >
                {filterItem.title}
              </Typography>
            )
          )}
        </Filters>
        <SendButton>
          <StyledLoadingButton
            variant="contained"
            color="secondary"
            fullWidth
            onClick={() => setIsAddNewTruckModalOpen(true)}
          >
            <Typography
              fontSize={16}
              fontWeight={500}
              color={colors.base.white}
            >
              Load New Truck
            </Typography>
          </StyledLoadingButton>
        </SendButton>
      </StyledHeader>
      <ListBoundary
        isEmpty={trucksData.length === 0 || !trucksData}
        isDataLoading={trucksLoadind}
        fallbackImageURL="/assets/images/emptyStates/Carriers-List EmptyState.svg"
        description="No truck added yet!"
      >
        <StyledTable
          disableColumnMenu
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableVirtualization
          disableSelectionOnClick
          loading={false}
          columns={columns}
          experimentalFeatures={{ newEditingApi: true }}
          rows={trucksData}
          onPageChange={(e) => setPage(e + 1)}
          page={page - 1}
          paginationMode="server"
          rowCount={trucksMeta?.pagination?.total_items}
        />
      </ListBoundary>

      <ConfirmModal
        title={"Remove truck"}
        iconName={"ModalRemove"}
        open={isDeletetModalOpen}
        isLoading={
          isGetShipmentCarriersLoading || isDeleteShipmentCarriersLoading
        }
        onDismiss={() => setIsDeletetModalOpen(false)}
        onConfirm={() => onDelete(selectedId)}
        confrimText={"Remove"}
        cancelText={"Cancel"}
      >
        <Typography fontSize={14} fontWeight={400} color={colors.gray[450]}>
          Are you sure you want to remove truck information of truck{" "}
          <b>{selectedName}</b> ?
        </Typography>
      </ConfirmModal>

      <AddNewTruckModal
        open={isAddNewTruckModalOpen}
        loading={isAddShipmentCarriersLoading}
        onAddVariant={(regNumber) => onAddNewCarrier(regNumber)}
        onDismiss={() => setIsAddNewTruckModalOpen(false)}
      />
    </>
  );
};

const StyledHeader = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: 58px;
  margin-bottom: 24px;
  ${(props) => props.theme.breakpoints.down("md")} {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SendButton = styled(Box)`
  ${(props) => props.theme.breakpoints.down("md")} {
    width: 100%;
    margin-top: 24px;
  }
`;

const Filters = styled("div")`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 32px;
  ${(props) => props.theme.breakpoints.down("sm")} {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FiltersLabel = styled("span")`
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  white-space: nowrap;
  color: ${colors.midnight[850]};
  margin-right: 50px;
`;

const StyledAcitve = styled(Box)`
  background-color: ${colors.midnight[20]};
  border-radius: 4px;
  padding: 8px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const FilterBadge = styled(Box)`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  padding: 3px 8px;
  width: auto;
  height: 24px;
  background-color: ${colors.secondary[700]};
  color: ${colors.base.white};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
